import React, { useEffect, useState } from 'react';
import Footer from '../containers/footer';
import Header from '../containers/header';
import Loading from '../components/loading/loading.js';
import { getHomeData } from '../services/home.js';
import { isAdmin, getLocale } from '../helpers/helpers_general.js';
import { Box, Chip, Typography } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PremiumIcon from '../assets/images/icons/PremiumIcon.svg';
import BookInformation from '../components/book_card/book_information.js';
import EditUserModal from '../components/modals/EditUserModal.js';
import { deleteUser } from '../services/users.js';
import DeleteConfirmModal from '../components/modals/DeleteConfirmModal.js';
import { useAlert } from '../context';
import { AlertSuccess, AlertError } from '../components/Alerts';

const paddingSx = {
  padding: '0 160px',
  '@media (max-width: 1280px)': { padding: '0 80px' },
  '@media (max-width: 1024px)': { padding: '0 20px' },
  '@media (max-width: 600px)': { padding: '0 12px' },
};

const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
const isRecent = dateStr => dateStr && new Date(dateStr) >= sevenDaysAgo;

const pulseGreen = {
  '@keyframes pulseGreen': {
    '0%, 100%': { borderColor: '#e0e0e0', backgroundColor: '#fff' },
    '50%': { borderColor: '#66bb6a', backgroundColor: '#f1f8f1' },
  },
};

const formatDateTime = dateStr => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const date = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
};

const Last7Badge = ({ count }) => {
  if (!count) return null;
  return (
    <Chip
      label={`+${count} nos últimos 7 dias`}
      size="small"
      sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '11px', height: '20px' }}
    />
  );
};

const SectionTitle = ({ icon, iconColor, children, badge }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '12px', pb: '8px', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap' }}>
    <Box sx={{ color: iconColor, display: 'flex', alignItems: 'center' }}>{icon}</Box>
    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
      {children}
    </Typography>
    {badge}
  </Box>
);

const ContentItem = ({ content, onClick }) => {
  const recent = isRecent(content.latest_doc_at || content.created_at);
  return (
  <Box
    onClick={() => onClick(content)}
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 14px', mb: '6px', borderRadius: '8px',
      border: '1px solid #e0e0e0', backgroundColor: '#fff',
      cursor: 'pointer', transition: 'box-shadow 0.15s',
      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderColor: '#006494' },
      ...(recent ? { ...pulseGreen, animation: 'pulseGreen 2s ease-in-out infinite' } : {}),
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#3E3D3D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {content.title}
        </Typography>
        {content.premium && <img src={PremiumIcon} alt="premium" style={{ width: '12px', flexShrink: 0 }} />}
      </Box>
      {content.category_slug && (
        <Typography sx={{ fontSize: '11px', color: '#9e9e9e', textTransform: 'capitalize', mt: '2px' }}>
          {content.category_slug}
        </Typography>
      )}
    </Box>
    <Typography sx={{ fontSize: '11px', color: '#bdbdbd', flexShrink: 0, ml: '12px' }}>
      {formatDateTime(content.latest_doc_at || content.created_at)}
    </Typography>
  </Box>
  );
};

const UserItem = ({ user, onClick }) => {
  const recent = isRecent(user.created_at);
  return (
  <Box
    onClick={() => onClick(user)}
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 14px', mb: '6px', borderRadius: '8px',
      border: '1px solid #e0e0e0', backgroundColor: '#fff',
      cursor: 'pointer', transition: 'box-shadow 0.15s',
      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderColor: '#2e7d32' },
      ...(recent ? { ...pulseGreen, animation: 'pulseGreen 2s ease-in-out infinite' } : {}),
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#3E3D3D' }}>
          {user.name}
        </Typography>
        {user.company && (
          <Chip label={user.company} size="small" sx={{ backgroundColor: '#f0f4f8', color: '#555', fontSize: '10px', height: '16px' }} />
        )}
      </Box>
      <Typography sx={{ fontSize: '11px', color: '#9e9e9e', mt: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {user.email}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, ml: '8px' }}>
      {user.premium && <img src={PremiumIcon} alt="premium" style={{ width: '12px' }} />}
      <Typography sx={{ fontSize: '11px', color: '#bdbdbd' }}>
        {formatDateTime(user.created_at)}
      </Typography>
    </Box>
  </Box>
  );
};

const Card = ({ title, icon, iconColor, children, badge }) => (
  <Box sx={{
    backgroundColor: '#fafafa', borderRadius: '12px',
    border: '1px solid #e0e0e0', padding: '20px',
    flex: 1, minWidth: 0, width: '100%', boxSizing: 'border-box',
    display: 'flex', flexDirection: 'column',
    maxHeight: 'calc(100vh - 200px)',
    '@media (max-width: 600px)': { maxHeight: '60vh' },
  }}>
    <SectionTitle icon={icon} iconColor={iconColor} badge={badge}>{title}</SectionTitle>
    <Box sx={{ overflowY: 'auto', flex: 1 }}>
      {children}
    </Box>
  </Box>
);

const HomeView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const admin = isAdmin();
  const { setShowSuccess, setShowError, setTextSuccessAlert, setTextErrorAlert, textSuccessAlert, textErrorAlert } = useAlert();

  useEffect(() => {
    getHomeData().then(res => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleDeleteUser = () => {
    deleteUser(selectedUser.id).then(
      () => {
        setShowSuccess(true);
        setTextSuccessAlert('Usuário deletado com sucesso!');
        setShowDeleteModal(false);
        setTimeout(() => window.location.reload(), 1400);
      },
      () => {
        setShowError(true);
        setTextErrorAlert('Erro ao deletar o usuário.');
        setShowDeleteModal(false);
      }
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', overflow: 'hidden' }}>
      <Header loggedIn={true} />

      {/* Hero banner — estende-se por baixo dos cards via marginBottom negativo */}
      <Box sx={{
        marginTop: '48px',
        background: 'linear-gradient(to bottom, #006494 0%, #005580 30%, #c8dfed 70%, #f5f5f5 100%)',
        ...paddingSx,
        padding: '40px 160px 220px',
        marginBottom: '-180px',
        '@media (max-width: 1280px)': { padding: '40px 80px 220px' },
        '@media (max-width: 1024px)': { padding: '32px 20px 200px' },
        '@media (max-width: 600px)': { padding: '24px 12px 160px', marginTop: '56px', marginBottom: '-130px' },
      }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.12em', mb: '8px' }}>
          Biblioteca Virtual
        </Typography>
        <Typography variant="h1" sx={{ fontWeight: 700, fontSize: '24px', color: '#fff', lineHeight: 1.4, maxWidth: '560px',
          '@media (max-width: 600px)': { fontSize: '18px' },
        }}>
          {getLocale() === 'es'
            ? 'Seu repositorio privado de documentos relevantes para el sector de saneamiento.'
            : 'Seu repositório privado de documentos relevantes para o setor de saneamento.'}
        </Typography>
      </Box>

      <Box sx={{ marginBottom: '32px', marginTop: '0', position: 'relative', zIndex: 1, ...paddingSx, '@media (max-width: 600px)': { marginTop: '0' } }}>

        {loading ? (
          <Loading />
        ) : (
          <Box sx={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexDirection: 'row', '@media (max-width: 600px)': { flexDirection: 'column' } }}>
            {/* Últimos conteúdos */}
            <Card
              title="Últimos conteúdos ou arquivos adicionados"
              icon={<ArticleOutlinedIcon fontSize="small" />}
              iconColor="#006494"
              badge={<Last7Badge count={data?.contents_last_7_days} />}
            >
              {data?.recent_contents?.length > 0 ? (
                data.recent_contents.map(c => (
                  <ContentItem key={c.id} content={c} onClick={setSelectedContent} />
                ))
              ) : (
                <Typography sx={{ color: '#9e9e9e', fontSize: '13px' }}>Nenhum conteúdo encontrado.</Typography>
              )}
            </Card>

            {/* Últimos usuários — só admin */}
            {admin && (
              <Card
                title="Últimos usuários cadastrados"
                icon={<PeopleOutlinedIcon fontSize="small" />}
                iconColor="#2e7d32"
                badge={<Last7Badge count={data?.users_last_7_days} />}
              >
                {data?.recent_users?.length > 0 ? (
                  data.recent_users.map(u => (
                    <UserItem key={u.id} user={u} onClick={setSelectedUser} />
                  ))
                ) : (
                  <Typography sx={{ color: '#9e9e9e', fontSize: '13px' }}>Nenhum usuário encontrado.</Typography>
                )}
              </Card>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ flexGrow: 1 }} />
      <Footer />

      {/* Modal visualização de conteúdo */}
      {selectedContent && (
        <BookInformation
          showContentModal={!!selectedContent}
          setModalStates={() => setSelectedContent(null)}
          contentId={selectedContent.id}
          title={selectedContent.title}
          description={selectedContent.description}
        />
      )}

      {/* Modal edição de usuário */}
      {selectedUser && (
        <EditUserModal
          showUserModal={!!selectedUser}
          setStates={() => setSelectedUser(null)}
          user={selectedUser}
          onDelete={() => { setSelectedUser(null); setShowDeleteModal(true); }}
        />
      )}

      <DeleteConfirmModal
        showModal={showDeleteModal}
        setStates={s => setShowDeleteModal(s.showModal ?? false)}
        deleteCard={handleDeleteUser}
        title={selectedUser?.name}
      />

      <AlertSuccess textAlert={textSuccessAlert} />
      <AlertError textAlert={textErrorAlert} />
    </Box>
  );
};

export default HomeView;
